import { RouterOutputs } from '@crocodille/api';
import { isSome, Maybe } from '@crocodille/util';
import { parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import useSWR, { unstable_serialize } from 'swr';
import useSWRInfinite, {
  unstable_serialize as unstable_serialize_infinite,
} from 'swr/infinite';
import useSWRMutation from 'swr/mutation';

import { App } from '../app';
import { useApp } from '../app-provider';
import {
  OrderScoreFormData,
  useOrderScoreForm,
} from '../forms/order-score-form.v2';
import { useLagging } from '../hooks/use-lagging';
import { laggy } from '../util/swr';
import { useCartOrderIdentifier } from './cart';
import { ShippingMethod } from '@crocodille/api/lib/model/shipping';

export type Order = NonNullable<RouterOutputs['order']['byTokenValue']>;
export type OrderUpdate =
  RouterOutputs['order']['trackingByTokenValue']['orderUpdate'];

export function getOrderKey(tokenValue: Maybe<string> | false) {
  return tokenValue ? (['order.byTokenValue', tokenValue] as const) : false;
}

export function useOrder(tokenValue: Maybe<string> | false) {
  const { trpc } = useApp();

  const response = useSWR(
    getOrderKey(tokenValue),
    async ([_, tokenValue]) => {
      const order = await trpc.order.byTokenValue.query(tokenValue);

      return order;
    },
    {
      revalidateOnMount: true,
      refreshInterval(latestData) {
        if (
          latestData?.displayNumber ||
          latestData?.state === 'cart' ||
          latestData?.state === 'cancelled' ||
          latestData?.state === 'new'
        ) {
          return 0;
        }

        return 1_000;
      },
    },
  );

  return response;
}

export async function fetchOrder(app: App, tokenValue: string) {
  const { trpc } = app;

  const order = await trpc.order.byTokenValue.query(tokenValue);

  return order;
}

export function asOrderFallback(
  order: NonNullable<Awaited<ReturnType<typeof fetchOrder>>>,
) {
  return {
    [unstable_serialize(getOrderKey(order.tokenValue))]: order,
  };
}

const ORDER_STATE_PRIORITY: Record<string, number> = {
  cart: 0,
  new: 10,
  fulfilled: 20,
  cancelled: 1000,
};

const ORDER_SHIPPING_STATE_PRIORITY: Record<string, number> = {
  cart: 0,
  shipping_requested: 10,
  acquiring_transportation: 20,
  getting_ready: 30,
  ready: 40,
  shipping: 50,
  arrived: 60,
  shipped: 70,
  cancelled: 1000,
};

export function mergeOrderUpdate(
  current: Order | undefined,
  update: OrderUpdate,
) {
  if (!current) {
    return current;
  } else {
    return {
      ...current,
      ...(update ?? {}),
      state:
        update.state &&
        (ORDER_STATE_PRIORITY[update.state] ?? 0) >
          (ORDER_STATE_PRIORITY[current.state] ?? 0)
          ? update.state
          : current.state,
      shippingState:
        update.shippingState &&
        (ORDER_SHIPPING_STATE_PRIORITY[update.shippingState] ?? 0) >
          (ORDER_SHIPPING_STATE_PRIORITY[current.shippingState] ?? 0)
          ? update.shippingState
          : current.shippingState,
    };
  }
}

export type OrderTracking = NonNullable<ReturnType<typeof useTracking>['data']>;

export function useTracking(tokenValue: Maybe<string> | false) {
  const { trpc } = useApp();

  const { data: order, mutate } = useOrder(tokenValue);

  const { data: tracking } = useSWR(
    order?.tokenValue
      ? ['order.trackingByTokenValue', order.tokenValue]
      : false,
    async ([_, tokenValue]) => {
      const tracking = await trpc.order.trackingByTokenValue.query({
        tokenValue,
      });

      return tracking;
    },
    {
      use: [laggy],
      keepPreviousData: true,
      refreshInterval: (latestData) =>
        (latestData?.refreshInterval ?? 1_000) * 10, // 10x the returned time in order to avoid too many requests
      refreshWhenHidden: true,
      onSuccess: (data) => {
        if (data?.orderUpdate) {
          mutate(order ? mergeOrderUpdate(order, data.orderUpdate) : order, {
            revalidate: false,
          });
        }
      },
    },
  );

  const loadedOrderNumber = order?.orderNumber;

  const { data: courierPositionData } = useSWR(
    loadedOrderNumber
      ? ['order.courierPositionByOrderNumber', loadedOrderNumber]
      : false,
    async ([_, orderNumber]) => {
      const response = await trpc.order.courierPositionByOrderNumber.query({
        orderNumber,
      });

      return response;
    },
    {
      revalidateOnFocus: true,
      revalidateIfStale: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
      refreshInterval:
        order?.deliveryMethodType === 'courier'
          ? tracking?.refreshInterval ?? 1_000
          : undefined,
      onSuccess: (data) => {
        if (data?.orderUpdate) {
          mutate(order ? mergeOrderUpdate(order, data.orderUpdate) : order, {
            revalidate: false,
          });
        }
      },
    },
  );

  const courierPosition = useLagging(courierPositionData);

  const refresh = useCallback(() => {
    mutate(undefined, {
      revalidate: true,
    });
  }, [mutate]);

  const data = useMemo(() => {
    if (!tracking) {
      return tracking;
    }

    const eta = courierPosition?.courierEta ?? tracking?.courierEta;
    const courierEta = eta ? parseISO(eta) : null;
    const issueTo = tracking?.issueTo ? parseISO(tracking.issueTo) : null;
    const updatedAt = tracking?.updatedAt ? parseISO(tracking.updatedAt) : null;

    return {
      ...tracking,
      courierPosition:
        courierPosition?.courierPosition ?? tracking?.courierPosition,
      courierEta,
      updatedAt,
      issueTo: order?.deliveryMethodType === 'courier' ? courierEta : issueTo,
    };
  }, [
    tracking,
    courierPosition?.courierEta,
    courierPosition?.courierPosition,
    tracking?.courierEta,
    tracking?.issueTo,
    tracking?.updatedAt,
    order?.deliveryMethodType,
  ]);

  return {
    data,
    refresh,
  };
}

export type OrderState = NonNullable<
  RouterOutputs['order']['byTokenValue']
>['state'];

export type ShippingState = NonNullable<
  RouterOutputs['order']['byTokenValue']
>['shippingState'];

export function fetchOrders(app: App, page = 0) {
  return app.trpc.order.orders.query({ page: page + 1 });
}

function getOrderListKey(page: number) {
  return ['order.orders', { page }] as const;
}

type FetchOrdersResponse = {
  orders: Array<{
    id: number;
    state: string;
    tokenValue: string | null;
    number: string;
    checkoutCompletedAt: string | null;
    itemsTotal: number | null;
    currencyCode: string;
    total: number | null;
    instantDelivery?: boolean | null;
  }>;
  lastPage?: string;
};

function postProcessListedOrder(order: FetchOrdersResponse['orders'][number]) {
  return {
    ...order,
    checkoutCompletedAt: order.checkoutCompletedAt
      ? parseISO(order.checkoutCompletedAt)
      : null,
  };
}

export type ListedOrder = ReturnType<typeof postProcessListedOrder>;

function postProcessListedOrders(
  orders: Maybe<Array<FetchOrdersResponse['orders'][number]>>,
) {
  return orders?.map(postProcessListedOrder);
}

export function useOrders() {
  const app = useApp();

  const { data, ...ordersResponse } = useSWRInfinite<FetchOrdersResponse>(
    (index, previousPageData) => {
      if (previousPageData && !previousPageData.lastPage) {
        return null;
      }
      return getOrderListKey(index);
    },
    async ([_, { page }]: [string, { page: number }]) => {
      const response = await fetchOrders(app, page);
      return response;
    },
    {
      use: [laggy],
    },
  );

  const hasMore = useMemo(() => {
    if (!data || data.length === 0) return false;
    const lastPageData = data[data.length - 1];
    return Boolean(lastPageData && lastPageData.lastPage);
  }, [data]);

  const orders = useMemo(() => {
    if (!data || !Array.isArray(data)) return undefined;
    const allOrders = data.flatMap((page) => page.orders);
    return postProcessListedOrders(allOrders);
  }, [data]);

  return {
    data: orders,
    hasMore,
    ...ordersResponse,
  };
}

export async function fetchOrdersFallback(app: App, page = 0) {
  return {
    [unstable_serialize_infinite(getOrderListKey)]: await fetchOrders(
      app,
      page,
    ),
  };
}

export function useLatestRepeatableOrder(fetch = true) {
  const app = useApp();

  const { data, ...ordersResponse } = useSWR(
    fetch ? (['order.latestRepeatableOrder'] as const) : false,
    async () => {
      const order = await app.trpc.order.latestRepeatableOrder.query();

      return order;
    },
    {
      use: [laggy],
    },
  );

  return {
    data: useMemo(
      () => (isSome(data) ? postProcessListedOrder(data) : data),
      [data],
    ),
    ...ordersResponse,
  };
}

export function useOrderRepeat(
  tokenValue: Maybe<string> | false,
  onSuccess?: () => void,
) {
  const [_, setOrderIdentifier] = useCartOrderIdentifier();
  const { trpc, locale } = useApp();

  const [result, setResult] =
    useState<Maybe<Omit<RouterOutputs['order']['repeat'], 'order'>>>(null);

  const { trigger, isMutating, error } = useSWRMutation(
    ['order.repeat', { tokenValue, locale }],
    async (_, { arg: { tokenValue } }: { arg: { tokenValue: string } }) => {
      const orderRepeat = await trpc.order.repeat.mutate({
        tokenValue,
        locale,
      });

      return orderRepeat;
    },
    {
      onSuccess: ({ order, unavailableItems, couponErrors }) => {
        setOrderIdentifier({
          tokenValue: order.tokenValue,
          branchCode: order.branchCode,
        });

        setResult({
          unavailableItems,
          couponErrors,
        });

        onSuccess?.();
      },
    },
  );

  const { data: orderRepeatable } = useSWR(
    tokenValue ? (['order.repeatable', { tokenValue }] as const) : false,
    async ([_, { tokenValue }]) => {
      const orderRepeatable = await trpc.order.repeatable.query({
        tokenValue,
      });

      return orderRepeatable;
    },
  );

  const repeatOrder = useCallback(async () => {
    if (tokenValue) {
      await trigger({ tokenValue });
    }
  }, [tokenValue]);

  const clear = useCallback(() => {
    setResult(null);
  }, []);

  return {
    repeatOrder,
    result,
    clear,
    isMutating,
    error,
    canRepeat: orderRepeatable,
  };
}

export function useOrderScore(
  tokenValue: string,
  initialScore?: {
    readonly productScore?: Maybe<number>;
    readonly deliveryScore?: Maybe<number>;
  },
) {
  const { trpc, locale } = useApp();
  const { data: order } = useOrder(tokenValue);

  const {
    data: success,
    trigger,
    isMutating,
    error,
  } = useSWRMutation<
    boolean,
    Error,
    ['order.score', { tokenValue: string; locale: string }],
    OrderScoreFormData
  >(
    ['order.score', { tokenValue, locale }],
    async (_, { arg: { comment, productScore, deliveryScore } }) => {
      if (!productScore) {
        return false;
      }

      await trpc.order.score.mutate({
        tokenValue,
        locale,
        productScore,
        deliveryScore,
        comment,
      });

      return true;
    },
    {},
  );

  const form = useOrderScoreForm({
    onSubmit: trigger,
    isCourierDelivery: order?.deliveryMethodType === 'courier',
    ...initialScore,
  });

  return {
    form,
    order,
    isMutating,
    error,
    success,
  };
}

export function getOrderMinValue(order: Maybe<Order>) {
  const minPriceAdjustment = order?.adjustments?.find?.(
    (a) => a.role === 'order_min_price',
  );

  if (!minPriceAdjustment || minPriceAdjustment.price === 0) {
    return null;
  }

  const nonIncludedAdjustmentPrice =
    order?.adjustments
      ?.filter((a) => a.role === 'shipping')
      ?.reduce((acc, a) => acc + a.price, 0) ?? 0;

  return (order?.total ?? 0) - nonIncludedAdjustmentPrice;
}

export function getDeliveryFromOrder(
  order: Order,
  inRestaurantShippingMethods?: ShippingMethod[],
  addressDataId?: string,
) {
  if (order.deliveryMethodType === 'atBranch' && inRestaurantShippingMethods) {
    if (!order.specialDelivery) {
      return {
        deliveryMethod: 'atBranch',
        shippingMethodId: inRestaurantShippingMethods.find(
          (shipping) => shipping.type === order.shippingMethodType,
        )?.id,
      };
    } else if (order.specialDelivery.type === 'gate_delivery') {
      return { deliveryMethod: 'gate', gate: order.specialDelivery.gate };
    } else if (order.specialDelivery.type === 'table_delivery') {
      return { deliveryMethod: 'table', table: order.specialDelivery.table };
    } else {
      return {
        deliveryMethod: 'trainStation',
        trainStationNumber: order.specialDelivery.trainStation,
      };
    }
  } else {
    return {
      deliveryMethod: 'courier',
      addressDataId: addressDataId,
    };
  }
}
